import React from "react";
import withStyles from "isomorphic-style-loader/withStyles";
import { connect } from "react-redux";
import { Row, Col, Button, FormGroup, Input, Label } from "reactstrap";
import { AlertColor } from "@mui/material";
import ModalFrame from "../../../toolkit/modalFrame";
import SnackbarNotification from "../../../toolkit/snackbarNotification";
import s from "./styles.scss";
import { ClipLoader } from "react-spinners";
import {
  updatePhotoUrl,
  updateProfile,
  adminOtpChallenge,
  confirmAdminPhone,
} from "../../../utils/fetch";
import Files from "react-files";
import classNames from "classnames";
import * as modalActions from "../../../utils/actions/modals";
import * as userActions from "../../../utils/actions/user";
import Reform from "@franleplant/reform";
import { serviceCaller, ServiceCallerProps } from "../../composition";
import { withFirebase } from "react-redux-firebase";
import ICharity from "../../../types/services/ICharity";
import phone from "phone";
import AdminOtpPanel from "../../../toolkit/adminOtpPanel";
import { formatAdminRoleLabel } from "../../../utils/roleCheck";
import { formatPhoneNumber } from "../../../utils/helpers";

interface Props extends ServiceCallerProps {
  isOpen: boolean;
  toggle: () => void;
  userName: string;
  firstName: string;
  lastName: string;
  photoUrl: string;
  currentCharity: ICharity;
  firebase: any;
  id: string;

  toggleUserProfileModal: () => void;
  toggleChangePasswordModal: () => void;
  updateCurrentPhotoUrl: (photoUrl: string) => void;
  updateCurrentProfile: (
    name: string,
    firstName: string,
    lastName: string
  ) => void;
}

type DisplayState = "normal";

const PHONE_MANDATORY_MESSAGE = "The phone number is mandatory.";

interface State {
  fields: {
    userName: string;
    firstName: string;
    lastName: string;
  };
  loaded: boolean;
  loading: boolean;
  loadingPhoto: boolean;
  displayState: DisplayState;
  phoneField: string;
  phoneOtpChallengeId: string | null;
  phoneOtpActive: boolean;
  phoneLoading: boolean;
  phoneInlineMessage: string | null;
  phoneIsError: boolean;
  dontRemindPhoneDismissed: boolean;
  phoneNumberVerified: boolean;
  verifiedPhoneDigits: string;
  profileUpdatePending: boolean;
  snackbarOpen: boolean;
  snackbarMessage: string;
  snackbarSeverity: AlertColor;
}

class UserProfile extends React.Component<Props, State> {
  re = Reform.reactMixins.objectMixin(this);

  state = {
    fields: {
      userName: "",
      firstName: "",
      lastName: "",
    },
    errors: {},
    loaded: true,
    loading: false,
    loadingPhoto: false,
    displayState: "normal" as DisplayState,
    phoneField: "",
    phoneOtpChallengeId: null,
    phoneOtpActive: false,
    phoneLoading: false,
    phoneInlineMessage: null,
    phoneIsError: false,
    dontRemindPhoneDismissed: false,
    phoneNumberVerified: false,
    verifiedPhoneDigits: "",
    profileUpdatePending: false,
    snackbarOpen: false,
    snackbarMessage: "",
    snackbarSeverity: "success",
  };

  validationRules = {
    userName: { required: true },
    firstName: { required: true },
    lastName: { required: true },
  };

  validationMessages = {
    required: _ => `Required`,
    default: _ => `Invalid`,
  };

  showSnackbar = (message: string, severity: AlertColor = "success") => {
    this.setState({
      snackbarOpen: true,
      snackbarMessage: message,
      snackbarSeverity: severity,
    });
  };

  hideSnackbar = () => {
    this.setState({ snackbarOpen: false });
  };

  showErrorToast = (message: string) => {
    this.showSnackbar(message, "error");
  };

  showPhoneFieldError = (message: string) => {
    if (message === PHONE_MANDATORY_MESSAGE) {
      this.setState({
        phoneInlineMessage: message,
        phoneIsError: true,
      });
      return;
    }
    this.setState({ phoneInlineMessage: null, phoneIsError: false });
    this.showErrorToast(message);
  };

  normalizeUsPhoneDigits = (value: string): string => {
    const digits = (value || "").replace(/\D/g, "");
    if (digits.length === 11 && digits.startsWith("1")) {
      return digits.substring(1);
    }
    return digits;
  };

  resolveAdminId = async (): Promise<string> => {
    const { id, firebase } = this.props;
    if (id) {
      return id;
    }
    const u = firebase.auth().currentUser;
    if (!u) {
      return "";
    }
    try {
      const tr = await u.getIdTokenResult(false);
      const realId = tr.claims?.realId;
      if (typeof realId === "string" && realId.length > 0) {
        return realId;
      }
    } catch {
      /* redux id preferred */
    }
    return u.uid;
  };

  hydratePhoneAndSettings = async () => {
    const { currentCharity, firebase } = this.props;
    if (!currentCharity?.id) {
      return;
    }
    const adminId = await this.resolveAdminId();
    if (!adminId) {
      return;
    }
    const uid = firebase.auth().currentUser?.uid;
    const db = firebase.database();
    let dismissSnap = { val: () => null as boolean | null };
    if (uid) {
      try {
        dismissSnap = await db
          .ref(`global/userSettings/${uid}/dontRemindPhone`)
          .once("value");
      } catch {
        dismissSnap = { val: () => null };
      }
    }
    let phoneSnap = { val: () => null as string | null };
    try {
      phoneSnap = await db
        .ref(`${currentCharity.id}/users/${adminId}/phoneNumber`)
        .once("value");
    } catch {
      phoneSnap = { val: () => null };
    }
    const v = phoneSnap.val();
    const phoneStr = v != null ? String(v) : "";
    const digits = this.normalizeUsPhoneDigits(phoneStr);
    const verified = digits.length >= 10;
    const displayPhone = phoneStr ? formatPhoneNumber(phoneStr) : "";
    this.setState({
      phoneField: displayPhone,
      dontRemindPhoneDismissed: dismissSnap.val() === true,
      phoneNumberVerified: verified,
      verifiedPhoneDigits: verified ? digits : "",
    });
  };

  phoneNeedsVerification = (): boolean => {
    const raw = (this.state.phoneField || "").trim();
    if (!raw) {
      return false;
    }
    const current = this.normalizeUsPhoneDigits(raw);
    if (current.length < 10) {
      return false;
    }
    if (!this.state.phoneNumberVerified) {
      return true;
    }
    return current !== this.state.verifiedPhoneDigits;
  };

  componentDidUpdate(prevProps: Props) {
    const modalJustOpened = !prevProps.isOpen && this.props.isOpen;
    const modalJustClosed = prevProps.isOpen && !this.props.isOpen;

    if (modalJustClosed) {
      this.setState({
        loaded: false,
        phoneField: "",
        snackbarOpen: false,
      });
    }

    if (modalJustOpened && this.props.config) {
      this.setState({
        loaded: true,
        phoneOtpActive: false,
        phoneOtpChallengeId: null,
        phoneLoading: false,
        phoneInlineMessage: null,
        phoneIsError: false,
        profileUpdatePending: false,
        fields: {
          userName: this.props.userName || "",
          firstName: this.props.firstName || "",
          lastName: this.props.lastName || "",
        },
      });
      void this.hydratePhoneAndSettings();
    } else if (this.props.userName && !this.state.fields.userName) {
      this.setState({
        fields: {
          userName: this.props.userName,
          firstName: !this.props.firstName ? "" : this.props.firstName,
          lastName: !this.props.lastName ? "" : this.props.lastName,
        },
      });
    }
  }

  onChangeFactory = fieldName => {
    return event => {
      const value = event.target.value;
      this.setState(state => {
        const fields = {
          ...state.fields,
          [fieldName]: value,
        };
        return { ...state, error: {}, fields };
      });
      this.re.validateField(fieldName, value);
    };
  };

  onPhoneFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ phoneField: e.target.value, phoneInlineMessage: null });
  };

  resumePhoneReminders = () => {
    const uid = this.props.firebase.auth().currentUser?.uid;
    if (!uid) {
      return;
    }
    this.props.firebase
      .database()
      .ref(`global/userSettings/${uid}/dontRemindPhone`)
      .remove()
      .then(() =>
        this.setState({
          dontRemindPhoneDismissed: false,
          phoneInlineMessage: null,
          phoneIsError: false,
        }, () => this.showSnackbar("Phone reminders are on again."))
      )
      .catch(() => this.showPhoneFieldError("Could not update reminder preference."));
  };

  attemptPhoneSave = async (): Promise<"done" | "otp" | "failed"> => {
    const raw = (this.state.phoneField || "").trim();
    if (!raw) {
      this.showPhoneFieldError("Enter a phone number.");
      return "failed";
    }
    const parsed = phone(raw, "US");
    if (!parsed.isValid) {
      this.showPhoneFieldError("Please enter a valid US phone number.");
      return "failed";
    }
    const e164 = parsed.phoneNumber;
    if (!this.props.config?.authServiceUrl) {
      this.showPhoneFieldError("Configuration not loaded.");
      return "failed";
    }
    this.setState({
      phoneLoading: true,
      phoneInlineMessage: null,
      phoneIsError: false,
    });
    try {
      const fbUser = this.props.firebase.auth().currentUser;
      if (!fbUser) {
        throw new Error("Not signed in.");
      }
      const firebaseToken = await fbUser.getIdToken(false);
      const ch = await adminOtpChallenge(this.props.config.authServiceUrl, {
        clientType: "web",
        operation: "phone",
        firebaseToken,
        newPhoneNumber: e164,
      });
      if (!ch.otpRequired && ch.completionToken) {
        await confirmAdminPhone(
          this.props.config.authServiceUrl,
          ch.completionToken
        );
        const digits = this.normalizeUsPhoneDigits(e164);
        this.setState({
          phoneLoading: false,
          phoneField: formatPhoneNumber(e164),
          phoneInlineMessage: null,
          phoneIsError: false,
          phoneNumberVerified: true,
          verifiedPhoneDigits: digits,
        });
        await this.hydratePhoneAndSettings();
        return "done";
      }
      if (ch.challengeId) {
        this.setState({
          phoneLoading: false,
          phoneOtpChallengeId: ch.challengeId,
          phoneOtpActive: true,
        });
        return "otp";
      }
      throw new Error("Unexpected verification response.");
    } catch (e: any) {
      this.setState({ phoneLoading: false });
      this.showPhoneFieldError(e?.message || String(e));
      return "failed";
    }
  };

  onPhoneOtpVerified = (completionToken: string) => {
    if (!this.props.config?.authServiceUrl) {
      return Promise.reject(new Error("Configuration not loaded."));
    }
    this.setState({ phoneLoading: true, phoneInlineMessage: null });
    return confirmAdminPhone(this.props.config.authServiceUrl, completionToken)
      .then(msg => {
        const pending = this.state.profileUpdatePending;
        this.setState({
          phoneLoading: false,
          phoneOtpActive: false,
          phoneOtpChallengeId: null,
          phoneInlineMessage: null,
          phoneIsError: false,
          profileUpdatePending: false,
        });
        return this.hydratePhoneAndSettings().then(() => {
          if (pending) {
            this.handleUpdateProfile();
          } else if (msg) {
            this.showSnackbar(msg);
          }
        });
      })
      .catch(err => {
        this.setState({ phoneLoading: false });
        this.showPhoneFieldError(err?.message || String(err));
        return Promise.reject(err);
      });
  };

  backFromPhoneOtp = () => {
    this.setState({
      phoneOtpActive: false,
      phoneOtpChallengeId: null,
      phoneInlineMessage: null,
      profileUpdatePending: false,
      loading: false,
    });
  };

  onSubmit = async e => {
    e.preventDefault();
    const isValid = this.re.validateFormFromState();
    if (!isValid) {
      return;
    }
    const rawPhone = (this.state.phoneField || "").trim();
    if (!rawPhone) {
      this.showPhoneFieldError(PHONE_MANDATORY_MESSAGE);
      return;
    }
    if (!phone(rawPhone, "US").isValid) {
      this.showPhoneFieldError("Please enter a valid US phone number.");
      return;
    }
    this.setState({
      loading: true,
      phoneInlineMessage: null,
      phoneIsError: false,
    });
    if (this.phoneNeedsVerification()) {
      this.setState({ profileUpdatePending: true });
      const phoneResult = await this.attemptPhoneSave();
      if (phoneResult === "otp") {
        this.setState({ loading: false });
        return;
      }
      if (phoneResult === "failed") {
        this.setState({ loading: false, profileUpdatePending: false });
        return;
      }
    }
    this.handleUpdateProfile();
  };

  changePassword = () => {
    this.props.toggleChangePasswordModal();
  };

  uploadPhoto = file => {
    if (file.length > 0) {
      this.setState({
        loadingPhoto: true,
      });
      this.props.firebase
        .storage()
        .ref()
        .child(`profilePictures/${this.props.id}.${file[0].extension}`)
        .put(file[0])
        .then(snapshot => {
          snapshot.ref.getDownloadURL().then(url => {
            updatePhotoUrl(
              this.props.config.authServiceUrl,
              this.props.serviceAccessToken,
              { photoUrl: url }
            )
              .then(response => {
                this.props.updateCurrentPhotoUrl(url);
                this.setState({ loadingPhoto: false });
                this.showSnackbar(response);
              })
              .catch(error => {
                this.setState({ loadingPhoto: false });
                this.showErrorToast(error);
              });
          });
        });
    }
  };

  onError = (error, _) => {
    this.setState({ loadingPhoto: false });
    this.showErrorToast(error.message);
  };

  handleUpdateProfile = () => {
    updateProfile(
      this.props.config.authServiceUrl,
      this.props.serviceAccessToken,
      {
        name: this.state.fields.firstName + " " + this.state.fields.lastName,
        firstName: this.state.fields.firstName,
        lastName: this.state.fields.lastName,
      }
    )
      .then(message => {
        this.props.updateCurrentProfile(
          this.state.fields.firstName + " " + this.state.fields.lastName,
          this.state.fields.firstName,
          this.state.fields.lastName
        );
        this.props.firebase
          .database()
          .ref()
          .child(this.props.currentCharity.id)
          .child("users")
          .child(this.props.id)
          .update({
            name:
              this.state.fields.firstName + " " + this.state.fields.lastName,
            firstName: this.state.fields.firstName,
            lastName: this.state.fields.lastName,
          })
          .then(
            () => {},
            error => {
              console.error(error);
            }
          );
        this.setState({
          loading: false,
        });
        this.showSnackbar(message);
      })
      .catch(error => {
        this.setState({ loading: false });
        this.showErrorToast(error);
      });
  };

  render() {
    return (
      <>
      <ModalFrame
        title="Profile Information"
        titleClassName={s.modalTitle}
        contentClassName={s.profileModalContent}
        size="lg"
        isOpen={this.props.isOpen}
        toggle={this.props.toggle}
      >
        {!this.state.loaded ? (
          <div className={s.centered}>
            <ClipLoader color={"#1f65b1"} size={28} loading />
          </div>
        ) : (
          <div>
            <Row className={s.photoSection}>
              <Col xs={12}>
                <div className={s.photoBlock}>
                  <div className={s.photoPreview}>
                    {!this.props.photoUrl ? (
                      <i
                        className={classNames("fa fa-user", s.photoPlaceholderIcon)}
                        aria-hidden
                      />
                    ) : (
                      <img
                        src={this.props.photoUrl}
                        alt="Profile"
                        className={s.photoImage}
                      />
                    )}
                    {this.state.loadingPhoto ? (
                      <div className={s.photoLoadingOverlay} aria-live="polite">
                        <ClipLoader color="#1f65b1" size={28} loading />
                      </div>
                    ) : null}
                  </div>
                  <div className={s.photoActions}>
                    <Files
                      ref={"files"}
                      className={s.photoFiles}
                      multiple={false}
                      clickable={!this.state.loadingPhoto}
                      accepts={["image/png", "image/jpeg"]}
                      maxFileSize={500000}
                      onError={this.onError}
                      onChange={this.uploadPhoto}
                    >
                      <button
                        type="button"
                        className={s.photoUploadButton}
                        disabled={this.state.loadingPhoto}
                      >
                        {this.state.loadingPhoto ? "Uploading…" : "Change photo"}
                      </button>
                    </Files>
                    <p className={s.photoHint}>PNG or JPEG, max 500 KB</p>
                  </div>
                </div>
              </Col>
            </Row>
            {this.state.displayState === "normal" && (
              <div>
                <div className={s.profileFieldsGrid}>
                  <div className={s.profileFieldColumn}>
                    <FormGroup>
                      <Label for="userName" className={s.label}>
                        Email
                      </Label>
                      <Input
                        name="Email"
                        id="userName"
                        value={this.state.fields.userName}
                        onChange={this.onChangeFactory("userName")}
                        disabled={true}
                        className={classNames(s.textField, s.roundedCorners)}
                      />
                    </FormGroup>
                    <ul className={s.fieldErrors}>
                      {this.re
                        .mapFieldErrors("userName")
                        .map((message, index) => {
                          return (
                            <li key={index} className={s.error}>
                              {message}
                            </li>
                          );
                        })}
                    </ul>

                    {this.state.dontRemindPhoneDismissed ? (
                      <p className={s.phoneReminderMuted}>
                        You turned off top-of-screen reminders about adding a
                        phone number.{" "}
                        <button
                          type="button"
                          className={s.inlineLink}
                          onClick={this.resumePhoneReminders}
                        >
                          Turn reminders back on
                        </button>
                      </p>
                    ) : null}
                    {this.state.phoneOtpActive &&
                    this.state.phoneOtpChallengeId &&
                    this.props.config?.authServiceUrl ? (
                      <AdminOtpPanel
                        authServiceUrl={this.props.config.authServiceUrl}
                        challengeId={this.state.phoneOtpChallengeId}
                        forceSmsOnly
                        initialChannel="sms"
                        onVerified={this.onPhoneOtpVerified}
                        onBack={this.backFromPhoneOtp}
                        intro="Enter the code we sent to verify this phone number."
                        showVerifiedSuccessScreen
                        verifiedSuccessTitle="Phone number confirmed"
                        verifiedSuccessBlurb="Saving your account…"
                      />
                    ) : (
                      <>
                        <FormGroup>
                          <Label for="profilePhone" className={s.label}>
                            Phone number
                          </Label>
                          <Input
                            id="profilePhone"
                            type="tel"
                            autoComplete="tel"
                            value={this.state.phoneField}
                            onChange={this.onPhoneFieldChange}
                            disabled={this.state.phoneLoading || this.state.loading}
                            className={classNames(s.textField, s.roundedCorners)}
                            placeholder="(555) 555-5555"
                          />
                        </FormGroup>
                        {this.state.phoneInlineMessage === PHONE_MANDATORY_MESSAGE &&
                        this.state.phoneIsError ? (
                          <div className={s.error}>
                            <span>{this.state.phoneInlineMessage}</span>
                          </div>
                        ) : null}
                      </>
                    )}
                  </div>

                  <div className={s.profileFieldColumn}>
                    <FormGroup>
                      <Label for="First Name" className={s.label}>
                        First Name
                      </Label>
                      <Input
                        name="First Name"
                        id="firstName"
                        value={this.state.fields.firstName}
                        onChange={this.onChangeFactory("firstName")}
                        className={classNames(s.textField, s.roundedCorners)}
                      />
                    </FormGroup>
                    <ul className={s.fieldErrors}>
                      {this.re
                        .mapFieldErrors("firstName")
                        .map((message, index) => {
                          return (
                            <li key={index} className={s.error}>
                              {message}
                            </li>
                          );
                        })}
                    </ul>

                    <FormGroup>
                      <Label for="Last Name" className={s.label}>
                        Last Name
                      </Label>
                      <Input
                        name="Last Name"
                        id="lastName"
                        value={this.state.fields.lastName}
                        onChange={this.onChangeFactory("lastName")}
                        className={classNames(s.textField, s.roundedCorners)}
                      />
                    </FormGroup>
                    <ul className={s.fieldErrors}>
                      {this.re
                        .mapFieldErrors("lastName")
                        .map((message, index) => {
                          return (
                            <li key={index} className={s.error}>
                              {message}
                            </li>
                          );
                        })}
                    </ul>
                  </div>

                  <FormGroup className={s.profileFieldFullWidth}>
                    <Label for="profileRole" className={s.label}>
                      Role
                    </Label>
                    <Input
                      id="profileRole"
                      name="Role"
                      value={formatAdminRoleLabel(this.props.currentCharity?.role)}
                      disabled
                      readOnly
                      className={classNames(s.textField, s.roundedCorners)}
                    />
                  </FormGroup>
                </div>

                <Row className={classNames(s.generalMargin, s.changePasswordRow)}>
                  <Col xs={12}>
                    <Button
                      className={classNames(s.button, s.bigButton)}
                      onClick={this.changePassword}
                      color={"none"}
                    >
                      Change Password
                    </Button>
                  </Col>
                </Row>
                {!this.state.phoneOtpActive ? (
                  <Row>
                    <Col xs={12} className={s.centerAlign}>
                      <Button
                        className={classNames(
                          s.updateButton,
                          s.bigButton,
                          s.button
                        )}
                        onClick={this.onSubmit}
                        disabled={this.state.loading || this.state.phoneLoading}
                        color={"none"}
                      >
                        {this.state.loading || this.state.phoneLoading
                          ? "Updating…"
                          : "Update Profile"}
                      </Button>
                    </Col>
                  </Row>
                ) : null}
              </div>
            )}
          </div>
        )}
      </ModalFrame>
      <SnackbarNotification
        open={this.state.snackbarOpen}
        message={this.state.snackbarMessage}
        severity={this.state.snackbarSeverity}
        onClose={this.hideSnackbar}
      />
      </>
    );
  }
}

const mapStateToProps = state => {
  const { user } = state;
  return {
    userName: user.email,
    firstName: user.firstName,
    lastName: user.lastName,
    photoUrl: user.photoUrl,
    currentCharity: user.currentCharity,
    id: user.id,
  };
};

const mapDispatchToProps = dispatch => ({
  toggleUserProfileModal: () => {
    dispatch(modalActions.toggleUserProfileModal());
  },
  updateCurrentPhotoUrl: (photoUrl = null) => {
    dispatch(userActions.updateCurrentPhotoUrl(photoUrl));
  },
  updateCurrentProfile: (name = null, firstName = null, lastName = null) => {
    dispatch(userActions.updateCurrentProfile(name, firstName, lastName));
  },
  toggleChangePasswordModal: () => {
    dispatch(modalActions.toggleChangePasswordModal());
  },
});

export default withFirebase(
  serviceCaller(
    connect<Props>(
      mapStateToProps,
      mapDispatchToProps
    )(withStyles(s)(UserProfile))
  )
);
